import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Breadcrumb from '@/components/feature/Breadcrumb';
import FilterPanel, { type FilterGroup } from '@/components/feature/FilterPanel';
import Pagination from '@/components/feature/Pagination';
import AnimeCard from '@/components/feature/AnimeCard';
import { animeList, categories, yearOptions, sortOptions } from '@/mocks/anime';
import { useDocumentMeta } from '@/hooks/useDocumentMeta';

const PAGE_SIZE = 8;

export default function Search() {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get('q') ?? '';
  const [input, setInput] = useState(query);
  const [values, setValues] = useState({ type: '全部', year: '全部' });
  const [sort, setSort] = useState('hot');
  const [visible, setVisible] = useState(PAGE_SIZE);

  useEffect(() => {
    setInput(query);
    setValues({ type: '全部', year: '全部' });
    setSort('hot');
    setVisible(PAGE_SIZE);
  }, [query]);

  const keyword = query.trim().toLowerCase();

  const groups: FilterGroup[] = [
    { key: 'type', label: '类型', options: ['全部', ...categories.map((item) => item.name)] },
    { key: 'year', label: '年份', options: ['全部', ...yearOptions.map(String)] },
  ];

  const results = useMemo(() => {
    if (!keyword) return [];
    let list = animeList.filter((anime) => {
      const haystack = `${anime.title} ${anime.description} ${anime.type} ${anime.region} ${anime.tags.join(' ')}`.toLowerCase();
      return haystack.includes(keyword);
    });
    if (values.type !== '全部') {
      list = list.filter((anime) => anime.type === values.type);
    }
    if (values.year !== '全部') {
      list = list.filter((anime) => anime.year === Number(values.year));
    }
    const sorted = [...list];
    if (sort === 'hot') {
      sorted.sort((a, b) => b.views - a.views);
    } else if (sort === 'rating') {
      sorted.sort((a, b) => b.rating - a.rating);
    } else {
      sorted.sort((a, b) => (a.updateDate < b.updateDate ? 1 : -1));
    }
    return sorted;
  }, [keyword, values, sort]);

  const shown = results.slice(0, visible);

  useDocumentMeta(
    query ? `“${query}”的搜索结果-动漫在线观看` : '搜索动漫-动漫在线观看',
    query
      ? `查看与“${query}”相关的动漫搜索结果，支持按类型与年份进一步筛选。`
      : '输入关键词搜索你想看的动漫，支持模糊匹配与多维筛选。',
    `${query || '动漫搜索'},动漫在线观看,动漫搜索,动漫资源,动漫筛选`,
  );

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    const value = input.trim();
    setSearchParams(value ? { q: value } : {});
  };

  const handleChange = (key: string, value: string) => {
    setValues((prev) => ({ ...prev, [key]: value }));
    setVisible(PAGE_SIZE);
  };

  const handleReset = () => {
    setValues({ type: '全部', year: '全部' });
    setSort('hot');
    setVisible(PAGE_SIZE);
  };

  return (
    <div className="w-full px-4 md:px-8 py-6 md:py-8">
      <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '搜索结果' }]} />

      <div className="mb-6">
        <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-4">
          搜索动漫
        </h1>
        <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3 max-w-2xl">
          <div className="relative flex-1">
            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-foreground-500 w-5 h-5 flex items-center justify-center">
              <i className="ri-search-line"></i>
            </span>
            <input
              value={input}
              onChange={(event) => setInput(event.target.value)}
              placeholder="输入动漫名称、类型或关键词"
              className="w-full pl-11 pr-4 py-3 rounded-md text-sm bg-background-50 border border-background-300 text-foreground-900 placeholder:text-foreground-500 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-300"
            />
          </div>
          <button
            type="submit"
            className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 font-medium whitespace-nowrap cursor-pointer transition-colors"
          >
            <i className="ri-search-line"></i>
            搜索
          </button>
        </form>
      </div>

      {keyword && (
        <p className="text-sm text-foreground-600 mb-4">
          {results.length > 0 ? (
            <>
              找到与 <span className="font-semibold text-primary-600">{query}</span> 相关的
              <span className="font-semibold text-primary-600"> {results.length} </span>部动漫
            </>
          ) : (
            <>
              未找到与 <span className="font-semibold text-accent-600">{query}</span> 相关的动漫
            </>
          )}
        </p>
      )}

      {keyword && (
        <FilterPanel
          groups={groups}
          values={values}
          onChange={handleChange}
          sortOptions={sortOptions}
          sortValue={sort}
          onSortChange={(value) => {
            setSort(value);
            setVisible(PAGE_SIZE);
          }}
          onReset={handleReset}
          resultCount={results.length}
        />
      )}

      <div className="mt-6">
        {!keyword ? (
          <div className="rounded-lg bg-background-50 border border-background-200 px-6 py-16 text-center">
            <span className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-primary-50 text-primary-500">
              <i className="ri-search-2-line text-3xl"></i>
            </span>
            <h2 className="font-heading text-lg font-semibold text-foreground-950 mb-2">
              开始搜索你喜欢的动漫
            </h2>
            <p className="text-sm text-foreground-500 mb-6">
              试试输入动漫名称、类型关键词，比如“科幻”“机甲”或“星海”。
            </p>
            <div className="flex flex-wrap items-center justify-center gap-2">
              {['科幻', '热血', '恋爱', '悬疑', '日常'].map((tag) => (
                <Link
                  key={tag}
                  to={`/search?q=${encodeURIComponent(tag)}`}
                  className="px-3 py-1.5 rounded-full text-sm bg-secondary-100 text-secondary-900 hover:bg-secondary-200 cursor-pointer transition-colors"
                >
                  {tag}
                </Link>
              ))}
            </div>
          </div>
        ) : shown.length > 0 ? (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {shown.map((anime) => (
                <AnimeCard key={anime.id} anime={anime} />
              ))}
            </div>
            <Pagination
              total={results.length}
              pageSize={PAGE_SIZE}
              visible={visible}
              onChangeVisible={setVisible}
            />
          </>
        ) : (
          <div className="rounded-lg bg-background-50 border border-background-200 px-6 py-16 text-center">
            <span className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-accent-50 text-accent-500">
              <i className="ri-emotion-sad-line text-3xl"></i>
            </span>
            <h2 className="font-heading text-lg font-semibold text-foreground-950 mb-2">
              没有找到相关动漫
            </h2>
            <p className="text-sm text-foreground-500 mb-6">
              换个关键词试试，或者去分类页浏览热门作品。
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                to="/category/少年"
                className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 font-medium whitespace-nowrap cursor-pointer transition-colors"
              >
                <i className="ri-layout-grid-line"></i>
                浏览分类
              </Link>
            </div>
          </div>
        )}
      </div>

      {!keyword && (
        <section className="mt-10">
          <h2 className="font-heading text-lg font-bold text-foreground-950 mb-4">热门推荐</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {[...animeList]
              .sort((a, b) => b.views - a.views)
              .slice(0, 4)
              .map((anime) => (
                <AnimeCard key={anime.id} anime={anime} />
              ))}
          </div>
        </section>
      )}
    </div>
  );
}